class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleDDSubmit = this.handleDDSubmit.bind(this);
    this.handleDDClick = this.handleDDClick.bind(this);
    this.handleCartTriggerClick = this.handleCartTriggerClick.bind(this);
    this.handleCartTriggerKeydown = this.handleCartTriggerKeydown.bind(this);
    this.handleDocumentKeyup = this.handleDocumentKeyup.bind(this);
    this.addEventListener('submit', this.handleDDSubmit);
    this.addEventListener('click', this.handleDDClick);
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    if (typeof ddCartUndo !== 'undefined') ddCartUndo.mount();
  }

  connectedCallback() {
    if (this.cartTriggerEventsBound) return;
    this.cartTriggerEventsBound = true;
    document.addEventListener('click', this.handleCartTriggerClick);
    document.addEventListener('keydown', this.handleCartTriggerKeydown);
    document.addEventListener('keyup', this.handleDocumentKeyup);
    this.cartNeedsRefresh = true;
    this.handleCartResume = () => {
      if (document.visibilityState !== 'visible') return;
      this.cartNeedsRefresh = true;
      if (this.classList.contains('active') && !this.querySelector('cart-drawer-items')?.isUpdating &&
          !Array.from(document.querySelectorAll('dd-pdp-product-form')).some((form) => form.isSubmitting || form.isCartPending)) {
        this.syncExternalCart();
      }
    };
    this.handleCartStorage = (event) => {
      if (event.key === 'dd:cart-updated') this.handleCartResume();
    };
    window.addEventListener('focus', this.handleCartResume);
    window.addEventListener('pageshow', this.handleCartResume);
    window.addEventListener('storage', this.handleCartStorage);
    document.addEventListener('visibilitychange', this.handleCartResume);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    window.removeEventListener('focus', this.handleCartResume);
    window.removeEventListener('pageshow', this.handleCartResume);
    window.removeEventListener('storage', this.handleCartStorage);
    document.removeEventListener('visibilitychange', this.handleCartResume);
    document.removeEventListener('click', this.handleCartTriggerClick);
    document.removeEventListener('keydown', this.handleCartTriggerKeydown);
    document.removeEventListener('keyup', this.handleDocumentKeyup);
    this.cartTriggerEventsBound = false;
  }

  handleDocumentKeyup(event) {
    if (event.code === 'Escape' && (this.escapeStartedInLightbox || this.querySelector('[data-dd-preserve-cart] dialog[open]'))) {
      this.escapeStartedInLightbox = false;
      return;
    }
    if (event.code === 'Escape' && this.classList.contains('active')) this.close();
  }

  handleCartTriggerClick(event) {
    const cartLink = event.target.closest?.('#cart-icon-bubble');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  handleCartTriggerKeydown(event) {
    if (event.code === 'Escape') {
      this.escapeStartedInLightbox = Boolean(this.querySelector('[data-dd-preserve-cart] dialog[open]'));
    }
    if (event.code.toUpperCase() !== 'SPACE') return;
    const cartLink = event.target.closest?.('#cart-icon-bubble');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  handleDDSubmit(event) {
    const recommendationForm = event.target.closest('[data-dd-mini-cart-recommendation-form]');
    if (recommendationForm) {
      event.preventDefault();
      this.addRecommendation(recommendationForm);
      return;
    }

    const discountForm = event.target.closest('[data-dd-mini-cart-discount-form]');
    if (!discountForm) return;
    event.preventDefault();
    this.updateDiscount(discountForm);
  }

  handleDDClick(event) {
    const removeDiscountButton = event.target.closest('[data-dd-mini-cart-remove-discount]');
    if (!removeDiscountButton) return;
    event.preventDefault();
    this.updateDiscount(null, removeDiscountButton);
  }

  async addRecommendation(form) {
    const submitButton = form.querySelector('[type="submit"]');
    const variantId = Number(new FormData(form).get('id'));
    if (!variantId || submitButton?.disabled) return;

    const defaultContent = submitButton?.querySelector('[data-dd-recommendation-default]');
    const pendingContent = submitButton?.querySelector('[data-dd-recommendation-pending]');
    const originalLabel = submitButton?.getAttribute('aria-label');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      if (pendingContent) submitButton.setAttribute('aria-label', pendingContent.textContent.trim());
    }
    if (defaultContent) defaultContent.hidden = true;
    if (pendingContent) pendingContent.hidden = false;
    this.clearDrawerError();

    try {
      const response = await fetch(`${this.shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: { _dd_add_placement: 'mini_cart' },
          }],
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || !state.sections) throw new Error(state.description || state.message);

      window.DDTracking?.add(state.items?.map((item) => ({ ...item, addedQuantity: 1 })), form);
      const committedState = await this.commitContents(state, {
        shouldOpen: false,
        focus: { context: 'drawer', selector: '.drawer__close' },
      });
      await this.publishCartUpdate(committedState, 'dd-mini-cart-recommendation');
    } catch (error) {
      this.showDrawerError(error?.message || this.dataset.ddRecommendationError);
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        if (originalLabel !== null) submitButton.setAttribute('aria-label', originalLabel);
      }
      if (defaultContent) defaultContent.hidden = false;
      if (pendingContent) pendingContent.hidden = true;
    }
  }

  async updateDiscount(form, trigger) {
    const isRemoval = !form;
    const input = form?.querySelector('[name="discount"]');
    const submitButton = form?.querySelector('[type="submit"]');
    const errorElement = form?.querySelector('[data-dd-mini-cart-discount-error]');
    const code = isRemoval ? '' : input?.value.trim();

    if (!isRemoval && !code) {
      this.showDiscountError(errorElement, this.dataset.ddPromoInvalid);
      input?.focus();
      return;
    }
    if (submitButton?.disabled || trigger?.disabled) return;

    if (submitButton) submitButton.disabled = true;
    if (trigger) trigger.disabled = true;
    this.showDiscountError(errorElement, '');

    try {
      const response = await fetch(`${this.shopRoot()}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: code,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || !state.sections) throw new Error(state.description || state.message);

      const appliedCodes = (state.cart_level_discount_applications || [])
        .filter((discount) => discount.type === 'discount_code')
        .map((discount) => String(discount.title || discount.code || '').toLowerCase());
      const codeWasApplied = isRemoval || appliedCodes.includes(code.toLowerCase());

      const focusSelector = isRemoval
        ? '[data-dd-mini-cart-discount-form] [name="discount"]'
        : '[data-dd-mini-cart-remove-discount]';
      const committedState = await this.commitContents(state, {
        shouldOpen: false,
        focus: {
          context: 'drawer',
          selector: codeWasApplied
            ? focusSelector
            : '[data-dd-mini-cart-discount-form] [name="discount"]',
        },
      });
      if (!codeWasApplied) {
        const refreshedDetails = this.querySelector('.dd-mini-cart__promo');
        if (refreshedDetails) refreshedDetails.open = true;
        const refreshedInput = this.querySelector('[data-dd-mini-cart-discount-form] [name="discount"]');
        const refreshedError = this.querySelector('[data-dd-mini-cart-discount-error]');
        if (refreshedInput) refreshedInput.value = code;
        this.showDiscountError(refreshedError, this.dataset.ddPromoInvalid);
        await this.publishCartUpdate(committedState, 'dd-mini-cart-discount');
        return;
      }

      await this.publishCartUpdate(committedState, 'dd-mini-cart-discount');
    } catch (_error) {
      if (submitButton) submitButton.disabled = false;
      if (trigger) trigger.disabled = false;
      this.showDiscountError(errorElement, this.dataset.ddPromoError);
      (input || trigger)?.focus();
    }
  }

  sectionIds() {
    return this.getSectionsToRender().map((section) => section.id);
  }

  publishCartUpdate(cartData, source, detail = {}) {
    if (source !== 'dd-cart-sync') {
      this.cartNeedsRefresh = false;
      try {
        localStorage.setItem('dd:cart-updated', `${Date.now()}:${Math.random()}`);
      } catch (_error) {
        // Focus/visibility refresh still works when browser storage is unavailable.
      }
    }
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return Promise.resolve();
    try {
      return Promise.resolve(
        publish(PUB_SUB_EVENTS.cartUpdate, {
          ...detail,
          source,
          cartData,
          sectionsRendered: true,
        })
      ).catch((error) => console.error(error));
    } catch (error) {
      console.error(error);
      return Promise.resolve();
    }
  }

  shopRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  showDiscountError(element, message) {
    if (!element) return;
    element.textContent = message || '';
    element.hidden = !message;
    const input = element.closest('form')?.querySelector('[name="discount"]');
    if (message) input?.setAttribute('aria-invalid', 'true');
    else input?.removeAttribute('aria-invalid');
  }

  captureInvalidPromotionStates() {
    return ['.dd-cart-summary--drawer', '.dd-cart-summary--page']
      .map((scopeSelector) => {
        const summary = document.querySelector(scopeSelector);
        const details = summary?.querySelector('.dd-mini-cart__promo');
        const input = summary?.querySelector('[data-dd-mini-cart-discount-form] [name="discount"]');
        const error = summary?.querySelector('[data-dd-mini-cart-discount-error]');
        const errorText = error?.textContent.trim() || '';
        const isInvalid = input?.getAttribute('aria-invalid') === 'true' || Boolean(errorText && !error.hidden);
        if (!isInvalid) return null;

        return {
          scopeSelector,
          isOpen: Boolean(details?.open),
          value: input?.value || '',
          errorText,
        };
      })
      .filter(Boolean);
  }

  restoreInvalidPromotionStates(states) {
    states.forEach(({ scopeSelector, isOpen, value, errorText }) => {
      const summary = document.querySelector(scopeSelector);
      const details = summary?.querySelector('.dd-mini-cart__promo');
      const input = summary?.querySelector('[data-dd-mini-cart-discount-form] [name="discount"]');
      const error = summary?.querySelector('[data-dd-mini-cart-discount-error]');
      if (!details || !input || !error) return;

      details.open = isOpen;
      input.value = value;
      input.setAttribute('aria-invalid', 'true');
      error.textContent = errorText;
      error.hidden = false;
    });
  }

  showDrawerError(message) {
    const errorElement = this.querySelector('#CartDrawer-CartErrors');
    if (errorElement) errorElement.textContent = message || this.dataset.ddRecommendationError || '';
  }

  clearDrawerError() {
    const errorElement = this.querySelector('#CartDrawer-CartErrors');
    if (errorElement) errorElement.textContent = '';
  }

  focusAfterInternalUpdate(selector) {
    this.focusAfterCartUpdate({ context: 'drawer', selector });
  }

  focusAfterCartUpdate(focus = {}) {
    const focusRequestId = (this.focusRequestId || 0) + 1;
    this.focusRequestId = focusRequestId;
    window.clearTimeout(this.focusAfterCartUpdateTimer);
    window.clearTimeout(this.focusConfirmationTimer);
    this.focusAfterCartUpdateTimer = window.setTimeout(() => {
      this.applyFocusAfterCartUpdate(focusRequestId, focus);
    }, 0);
  }

  applyFocusAfterCartUpdate(focusRequestId, focus) {
    if (this.focusRequestId !== focusRequestId) return;

    if (this.classList.contains('active')) {
      const container = this.querySelector('.drawer__inner');
      if (!container?.isConnected) return;
      const target =
        this.resolveFocusTarget(focus, container) ||
        container.querySelector('.cart-item__name, .dd-mini-cart__continue, .drawer__close') ||
        container;
      this.installDrawerFocusTrap(container, target, focus.select);
      this.confirmFocusAfterCartUpdate(focusRequestId, focus);
      return;
    }

    if (focus.context !== 'page') return;
    const page = document.getElementById('main-cart-items');
    const target =
      this.resolveFocusTarget(focus, page) ||
      page?.querySelector('.cart-item__name, .dd-cart-page__title, .dd-cart-page__continue');
    if (!target?.isConnected) return;
    if (target.matches('.dd-cart-page__title') && !target.hasAttribute('tabindex')) target.tabIndex = -1;
    target.focus({ preventScroll: true });
    if (focus.select && typeof target.select === 'function') target.select();
    this.confirmFocusAfterCartUpdate(focusRequestId, focus);
  }

  installDrawerFocusTrap(container, target, select = false) {
    delete this.dataset.ddFocusTrapped;
    removeTrapFocus();
    this.dataset.ddFocusTrapped = 'true';
    trapFocus(container, target);
    if (select && typeof target.select === 'function') target.select();
  }

  confirmFocusAfterCartUpdate(focusRequestId, focus) {
    window.clearTimeout(this.focusConfirmationTimer);
    this.focusConfirmationTimer = window.setTimeout(() => {
      if (this.focusRequestId !== focusRequestId) return;

      if (this.classList.contains('active')) {
        const container = this.querySelector('.drawer__inner');
        if (!container?.isConnected || container.contains(document.activeElement)) return;
        const target =
          this.resolveFocusTarget(focus, container) ||
          container.querySelector('.cart-item__name, .dd-mini-cart__continue, .drawer__close') ||
          container;
        this.installDrawerFocusTrap(container, target, focus.select);
        return;
      }

      if (focus.context !== 'page') return;
      const page = document.getElementById('main-cart-items');
      if (!page?.isConnected || page.contains(document.activeElement)) return;
      const target =
        this.resolveFocusTarget(focus, page) ||
        page.querySelector('.cart-item__name, .dd-cart-page__title, .dd-cart-page__continue');
      target?.focus?.({ preventScroll: true });
      if (focus.select && typeof target?.select === 'function') target.select();
    }, 0);
  }

  resolveFocusTarget(focus, root) {
    if (!root) return null;
    if (focus.selector) {
      const matched = root.querySelector(focus.selector);
      if (focus.useLineTitle) return matched?.closest('.dd-mini-cart-line')?.querySelector('.dd-mini-cart-line__title');
      return matched;
    }

    const escapedLineKey = focus.lineKey ? CSS.escape(String(focus.lineKey)) : '';
    const escapedVariantId = focus.variantId ? CSS.escape(String(focus.variantId)) : '';
    const input =
      (escapedLineKey && root.querySelector(`[data-quantity-line-key="${escapedLineKey}"]`)) ||
      (escapedVariantId && root.querySelector(`[data-quantity-variant-id="${escapedVariantId}"]`));
    if (!input) {
      const escapedFallbackLineKey = focus.fallbackLineKey ? CSS.escape(String(focus.fallbackLineKey)) : '';
      const escapedFallbackVariantId = focus.fallbackVariantId ? CSS.escape(String(focus.fallbackVariantId)) : '';
      const fallbackInput =
        (escapedFallbackLineKey && root.querySelector(`[data-quantity-line-key="${escapedFallbackLineKey}"]`)) ||
        (escapedFallbackVariantId && root.querySelector(`[data-quantity-variant-id="${escapedFallbackVariantId}"]`));
      if (focus.fallbackToLineTitle) {
        return fallbackInput?.closest('.dd-mini-cart-line')?.querySelector('.dd-mini-cart-line__title') || fallbackInput;
      }
      return fallbackInput;
    }
    if (!focus.controlName || focus.controlName === 'updates[]') return input;

    const quantityInput = input.closest('quantity-input');
    return quantityInput?.querySelector(`[name="${CSS.escape(focus.controlName)}"]`) || input;
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.ddCartBound === 'true') return;
    overlay.dataset.ddCartBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (this.isSyncingExternalCart && triggeredBy) return this.refreshContentsPromise;
    if (this.cartNeedsRefresh && !this.isSyncingExternalCart) {
      return this.syncExternalCart({ shouldOpen: true }, triggeredBy);
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.closeCompetingOverlays();
    this.bindOverlay();
    const focusRequestId = (this.focusRequestId || 0) + 1;
    this.focusRequestId = focusRequestId;
    this.isOpening = true;

    requestAnimationFrame(() => {
      if (this.focusRequestId !== focusRequestId) return;
      this.isOpening = false;
      // Keep native images eligible after first open, including while the drawer closes.
      this.setAttribute('data-dd-cart-media-ready', '');
      this.classList.add('animate', 'active');
      this.makeBackgroundInert();
      document.body.classList.add('overflow-hidden');
      // Dispatch only after this open is still current and visibly active.
      // Telemetry is optional while the item element or module is unavailable.
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    });

    const moveFocus = () => {
      if (this.focusRequestId !== focusRequestId) return;
      if (!this.classList.contains('active') || this.dataset.ddFocusTrapped === 'true') return;
      const containerToTrapFocusOn = this.querySelector('.drawer__inner');
      const focusElement = this.querySelector('.drawer__close') || containerToTrapFocusOn;
      if (!containerToTrapFocusOn || !focusElement) return;
      this.dataset.ddFocusTrapped = 'true';
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    this.querySelector('.drawer__inner')?.addEventListener('transitionend', moveFocus, { once: true });
    window.setTimeout(moveFocus, 360);

  }

  close() {
    this.querySelector('[data-dd-preserve-cart]')?.close?.(false);
    this.classList.remove('active');
    this.isOpening = false;
    this.focusRequestId = (this.focusRequestId || 0) + 1;
    window.clearTimeout(this.focusAfterCartUpdateTimer);
    window.clearTimeout(this.focusConfirmationTimer);
    delete this.dataset.ddFocusTrapped;
    document.body.classList.remove('overflow-hidden');
    this.restoreBackground();
    removeTrapFocus();
    this.restoreOpenerFocus();
  }

  restoreOpenerFocus() {
    const opener = this.activeElement?.isConnected
      ? this.activeElement
      : document.querySelector(this.activeElementSelector || '#cart-icon-bubble');
    const atc = opener?.closest('[data-dd-pdp-atc]');
    const productForm = opener?.closest('dd-pdp-product-form');
    const candidates = [
      opener,
      atc,
      ...Array.from(productForm?.querySelectorAll('[data-dd-pdp-submit], [data-dd-pdp-sticky-submit]') || []),
      document.getElementById('cart-icon-bubble'),
      document.getElementById('MainContent'),
      document.querySelector('footer'),
      document.body,
    ];
    const target = candidates.find((element) => {
      if (!element?.isConnected || !isElementVisibleForFocus(element)) return false;
      if (element.getAttribute('aria-disabled') === 'true') return false;
      const rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    });
    if (!target) return;

    // During Added, keep disabled purchase controls disabled and focus their visible group.
    const temporaryAttributes = [];
    const addTemporaryAttribute = (name, value) => {
      if (target.hasAttribute(name)) return;
      target.setAttribute(name, value);
      temporaryAttributes.push(name);
    };
    if (!target.matches('a[href], button, input, select, textarea, summary, [tabindex]')) {
      addTemporaryAttribute('tabindex', '-1');
    }
    if (target === atc) {
      addTemporaryAttribute('role', 'group');
      const label = productForm?.dataset.ddCartSuccess;
      if (label) addTemporaryAttribute('aria-label', label);
    }
    const cleanup = () => {
      temporaryAttributes.forEach((name) => target.removeAttribute(name));
      target.removeEventListener('blur', cleanup);
    };
    if (temporaryAttributes.length) target.addEventListener('blur', cleanup, { once: true });
    target.focus({ preventScroll: true });
    if (document.activeElement !== target) cleanup();
  }

  closeCompetingOverlays() {
    document.querySelectorAll('details-modal.header__search').forEach((searchModal) => {
      if (typeof searchModal.close === 'function' && searchModal.isOpen?.()) searchModal.close(false);
    });

    document.querySelectorAll('#Details-HeaderMenu-DD-Shop[open]').forEach((details) => {
      const megaMenu = details.querySelector('dd-mega-menu');
      if (typeof megaMenu?.close === 'function') megaMenu.close(false);
      else details.removeAttribute('open');
    });

    const mobileMenu = document.querySelector('header-drawer');
    if (mobileMenu?.mainDetailsToggle?.hasAttribute('open')) {
      const toggle = mobileMenu.mainDetailsToggle.querySelector('summary');
      mobileMenu.closeMenuDrawer?.(null, toggle);
      mobileMenu.mainDetailsToggle.removeAttribute('open');
    }
  }

  makeBackgroundInert() {
    this.inertedElements = [];
    Array.from(document.body.children).forEach((element) => {
      if (element === this || element.contains(this) || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(element.tagName)) return;
      if (element.hasAttribute('inert')) return;
      element.setAttribute('inert', '');
      this.inertedElements.push(element);
    });
  }

  restoreBackground() {
    this.inertedElements?.forEach((element) => element.removeAttribute('inert'));
    this.inertedElements = [];
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, renderOptions = true) {
    window.DDRefillRecommendations?.captureVisible();
    this.querySelector('[data-dd-preserve-cart]')?.close?.(false);
    const options = typeof renderOptions === 'object'
      ? { shouldOpen: true, ...renderOptions }
      : { shouldOpen: renderOptions };
    const wasOpen = this.classList.contains('active');
    const invalidPromotionStates = this.captureInvalidPromotionStates();
    const renderPlan = this.getSectionsToRender().map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      const sourceElement = html
        ? this.getSectionDOM(html, section.sourceSelector || section.selector || '.shopify-section')
        : null;

      if (!sectionElement || !sourceElement) {
        throw new Error(`Missing rendered cart section: ${section.id}`);
      }

      return { sectionElement, sourceElement };
    });

    if (wasOpen) {
      delete this.dataset.ddFocusTrapped;
      removeTrapFocus();
    }

    this.productId = parsedState.id;
    renderPlan.forEach(({ sectionElement, sourceElement }) => {
      sectionElement.innerHTML = sourceElement.innerHTML;
    });
    this.cartNeedsRefresh = false;

    const drawerIsEmpty = Boolean(this.querySelector('.drawer__inner-empty'));
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    this.classList.toggle('is-empty', drawerIsEmpty);
    mainCartItems?.classList.toggle('is-empty', Boolean(mainCartItems.querySelector('.dd-cart-page__empty')));
    mainCartFooter?.classList.toggle('is-empty', drawerIsEmpty);
    window.DDRefillRecommendations?.sync();
    this.restoreInvalidPromotionStates(invalidPromotionStates);
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    if (typeof ddCartUndo !== 'undefined') ddCartUndo.mount();

    if (!options.deferFocus && (wasOpen || options.focus?.context === 'page')) {
      this.focusAfterCartUpdate(options.focus || { context: 'drawer', selector: '.drawer__close' });
    } else if (options.shouldOpen) {
      setTimeout(() => {
        this.open();
      });
    }

    return parsedState;
  }

  async commitContents(parsedState, renderOptions = true) {
    try {
      return this.renderContents(parsedState, renderOptions);
    } catch (_error) {
      return this.refreshContents(renderOptions);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset.id) {
      sections.push({
        id: mainCartItems.dataset.id,
        selector: '#main-cart-items .js-contents',
        sourceSelector: '.js-contents',
      });
    }

    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  syncExternalCart(renderOptions = { shouldOpen: false }, triggeredBy) {
    if (this.isSyncingExternalCart) return this.refreshContentsPromise;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.cartNeedsRefresh = false;
    this.isSyncingExternalCart = true;
    return this.refreshContents(renderOptions)
      .then((state) => this.publishCartUpdate(state, 'dd-cart-sync'))
      .catch((error) => {
        console.error(error);
        if (renderOptions.shouldOpen) this.open(triggeredBy);
        this.cartNeedsRefresh = true;
        this.showDrawerError(this.dataset.ddCartError || window.cartStrings?.error || 'Unable to refresh cart.');
      })
      .finally(() => { this.isSyncingExternalCart = false; });
  }

  refreshContents(renderOptions = { shouldOpen: false }) {
    if (this.refreshContentsPromise) {
      return this.refreshContentsPromise.then((state) => {
        if (renderOptions.shouldOpen && !this.classList.contains('active')) this.open();
        else if (renderOptions.focus && !renderOptions.deferFocus) this.focusAfterCartUpdate(renderOptions.focus);
        return state;
      });
    }

    const sectionIds = this.sectionIds();
    const refreshPromise = fetch(`${this.shopRoot()}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates: {},
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
        const state = await response.json();
        if (!state || !Array.isArray(state.items) || !Number.isFinite(Number(state.item_count))) {
          throw new Error('Cart refresh returned an invalid cart state');
        }

        const sections = state.sections;
        const missingSectionIds = sectionIds.filter(
          (sectionId) => typeof sections?.[sectionId] !== 'string' || sections[sectionId].trim() === ''
        );
        if (missingSectionIds.length) {
          throw new Error(`Missing rendered cart sections: ${missingSectionIds.join(', ')}`);
        }

        return this.renderContents(state, renderOptions);
      })
      .finally(() => {
        if (this.refreshContentsPromise === refreshPromise) this.refreshContentsPromise = null;
      });

    this.refreshContentsPromise = refreshPromise;
    return refreshPromise;
  }

  setActiveElement(element) {
    this.activeElement = element;
    this.activeElementSelector = element?.id ? `#${CSS.escape(element.id)}` : null;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.connectScrollCues();
  }

  disconnectedCallback() {
    this.disconnectScrollCues();
    super.disconnectedCallback();
  }

  connectScrollCues() {
    this.disconnectScrollCues();
    if (!this.closest('cart-drawer[data-dd-mini-cart-refinement]') || !window.ResizeObserver) return;
    this.scrollCueBody = this.closest('.dd-mini-cart__body');
    this.scrollCuePanel = this.closest('.drawer__inner');
    if (!this.scrollCueBody || !this.scrollCuePanel) return;

    this.scrollCue = document.createElement('div');
    this.scrollCue.className = 'dd-mini-cart__scroll-cues';
    this.scrollCue.setAttribute('aria-hidden', 'true');
    this.scrollCue.hidden = true;
    this.scrollCuePanel.append(this.scrollCue);
    this.scrollCueMedia = window.matchMedia('(max-width: 749px)');
    this.queueScrollCueUpdate = () => {
      if (this.scrollCueFrame) return;
      this.scrollCueFrame = requestAnimationFrame(() => {
        this.scrollCueFrame = null;
        this.updateScrollCues();
      });
    };
    this.addEventListener('scroll', this.queueScrollCueUpdate, { passive: true });
    this.scrollCueBody.addEventListener('scroll', this.queueScrollCueUpdate, { passive: true });
    this.scrollCueMedia.addEventListener('change', this.queueScrollCueUpdate);
    this.scrollCueResize = new ResizeObserver(this.queueScrollCueUpdate);
    [this, this.scrollCueBody, this.querySelector('form')].filter(Boolean).forEach((element) => {
      this.scrollCueResize.observe(element);
    });
    this.queueScrollCueUpdate();
  }

  disconnectScrollCues() {
    cancelAnimationFrame(this.scrollCueFrame);
    this.scrollCueFrame = null;
    this.scrollCueResize?.disconnect();
    this.removeEventListener('scroll', this.queueScrollCueUpdate);
    this.scrollCueBody?.removeEventListener('scroll', this.queueScrollCueUpdate);
    this.scrollCueMedia?.removeEventListener('change', this.queueScrollCueUpdate);
    this.scrollCue?.remove();
    this.removeAttribute('data-dd-scroll-cues');
  }

  updateScrollCues() {
    if (!this.isConnected || !this.scrollCue) return;
    const mobile = this.scrollCueMedia.matches;
    const scroller = getComputedStyle(this).overflowY === 'auto' ? this : this.scrollCueBody;
    const viewportHeight = scroller.clientHeight;
    const maxScroll = scroller.scrollHeight - viewportHeight;
    const overflowing = mobile && viewportHeight > 0 && maxScroll > 1;
    this.scrollCue.hidden = !overflowing;
    this.toggleAttribute('data-dd-scroll-cues', overflowing && scroller === this);
    if (!overflowing) return;

    const viewport = scroller.getBoundingClientRect();
    const panel = this.scrollCuePanel.getBoundingClientRect();
    const scrollTop = Math.min(maxScroll, Math.max(0, scroller.scrollTop));
    const trackHeight = Math.max(0, viewportHeight - 8);
    const thumbHeight = Math.min(trackHeight, Math.max(24, trackHeight * viewportHeight / scroller.scrollHeight));
    const thumbOffset = (trackHeight - thumbHeight) * scrollTop / maxScroll;
    const productsBottom = this.querySelector('.dd-mini-cart__lines')?.getBoundingClientRect().bottom || 0;
    this.scrollCue.style.top = `${viewport.top - panel.top}px`;
    this.scrollCue.style.height = `${viewportHeight}px`;
    this.scrollCue.style.setProperty('--dd-scroll-thumb-height', `${thumbHeight}px`);
    this.scrollCue.style.setProperty('--dd-scroll-thumb-offset', `${thumbOffset}px`);
    this.scrollCue.toggleAttribute('data-more-below', maxScroll - scrollTop > 1 && productsBottom > viewport.bottom + 1);
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class DDRefillRecommendations {
  constructor() {
    this.storageKey = 'dd:refill-recommendations:v1';
    let saved = {};
    try {
      saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}') || {};
    } catch (_error) {}
    this.seen = new Map(Array.isArray(saved.seen) ? saved.seen : []);
    this.ignored = new Set(Array.isArray(saved.ignored) ? saved.ignored : []);
    this.quantities = new Map(Array.isArray(saved.quantities) ? saved.quantities : this.readQuantities());
    this.observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && this.isVisible(entry.target)) {
              this.remember(entry.target);
            }
          }
        }, { threshold: 0.5 })
      : null;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.sync());
    }
    this.sync();
  }

  readQuantities() {
    const root = document.querySelector('cart-drawer') || document.querySelector('dd-cart-page');
    const quantities = new Map();
    for (const input of root?.querySelectorAll('input[data-quantity-variant-id]') || []) {
      const id = String(input.dataset.quantityVariantId);
      quantities.set(id, (quantities.get(id) || 0) + Number(input.value || 0));
    }
    return [...quantities];
  }

  isVisible(item) {
    if (!item.isConnected || document.visibilityState === 'hidden') return false;
    const drawer = item.closest('cart-drawer');
    if (drawer ? !drawer.classList.contains('active') : document.querySelector('cart-drawer.active')) return false;
    const box = item.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return false;
    let left = Math.max(0, box.left);
    let top = Math.max(0, box.top);
    let right = Math.min(window.innerWidth, box.right);
    let bottom = Math.min(window.innerHeight, box.bottom);
    for (const parent of [item.closest('.dd-mini-cart__recommendation-viewport'), item.closest('[data-dd-mini-cart-body]')]) {
      if (!parent) continue;
      const clip = parent.getBoundingClientRect();
      left = Math.max(left, clip.left);
      top = Math.max(top, clip.top);
      right = Math.min(right, clip.right);
      bottom = Math.min(bottom, clip.bottom);
    }
    return Math.max(0, right - left) * Math.max(0, bottom - top) / (box.width * box.height) >= 0.5;
  }

  remember(item) {
    const handle = item.dataset.ddRecommendationProduct;
    const variant = item.querySelector('input[name="id"]')?.value;
    if (!handle || !variant || this.ignored.has(handle) || this.seen.get(handle) === String(variant)) return;
    this.seen.set(handle, String(variant));
    this.save();
  }

  captureVisible() {
    for (const item of document.querySelectorAll('[data-dd-recommendation-refill]')) {
      if (this.isVisible(item)) this.remember(item);
    }
  }

  sync() {
    const next = new Map(this.readQuantities());
    const added = new Set([...next].filter(([id, quantity]) => quantity > (this.quantities.get(id) || 0)).map(([id]) => id));
    if (added.size) {
      for (const [handle, variant] of this.seen) {
        if (!added.has(variant)) this.ignored.add(handle);
      }
    }
    this.quantities = next;
    this.save();
    this.applyOrder();
    this.observer?.disconnect();
    for (const item of document.querySelectorAll('[data-dd-recommendation-refill]')) this.observer?.observe(item);
  }

  applyOrder() {
    for (const track of document.querySelectorAll('.dd-mini-cart__recommendation-track')) {
      const items = [...track.children];
      const ignored = (item) => item.hasAttribute('data-dd-recommendation-refill') && this.ignored.has(item.dataset.ddRecommendationProduct);
      const ordered = [...items.filter((item) => !ignored(item)), ...items.filter(ignored)];
      if (ordered.every((item, index) => item === items[index])) continue;
      const focused = document.activeElement;
      const restoreFocus = track.contains(focused);
      track.append(...ordered);
      if (restoreFocus && document.activeElement !== focused) focused.focus({ preventScroll: true });
    }
  }

  save() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ seen: [...this.seen], ignored: [...this.ignored], quantities: [...this.quantities] }));
    } catch (_error) {}
  }
}

if (!window.DDRefillRecommendations) window.DDRefillRecommendations = new DDRefillRecommendations();
